<!-- Step 2: Select sensors or sensor groups -->
<div *ngIf="step === 2" class="wizard-step wizard-step-fill">
  <div class="step-title">{{ isEditMode ? 'Edit' : 'Create' }} Chart Favorite</div>
  <div class="step-subtitle">Select Sensors or Sensor Groups</div>

  <div class="preset-list">
    <label class="preset-check">
      <input type="checkbox" [(ngModel)]="cf.sensorPreset.recentlyAddedOrModified" (ngModelChange)="onPresetChanged()" />
      Search recently added or modified
    </label>
    <label class="preset-check">
      <input type="checkbox" [(ngModel)]="cf.sensorPreset.assignedToMeOrGroup" (ngModelChange)="onPresetChanged()" />
      Search sensors and sensor groups assigned to me or my user groups
    </label>
    <label class="preset-check">
      <input type="checkbox" [(ngModel)]="cf.sensorPreset.allSensorsAndGroups" (ngModelChange)="onPresetChanged()" />
      Search all sensors and sensor groups
    </label>
  </div>

  <div class="grow-panel list-panel">
    <div class="list-panel-header">
      <label>Sensors and Groups</label>
      <label class="list-panel-count">Selected Sensors: {{ cf.selectedSensors.length }}</label>
    </div>

    <div class="list-panel-body">
      <div class="list-search-row">
        <div class="select-all-toggle" (click)="toggleCheckedSensors()" title="Select / clear all">▣</div>
        <i class="fa fa-search list-search-icon" (click)="sensorSearchTextChanged()"></i>
        <form (ngSubmit)="sensorSearchTextChanged()">
          <input [(ngModel)]="searchSensorsText" placeholder="Search" type="text" name="searchSensorsText" autocomplete="off" class="list-search-input" />
        </form>
      </div>

      <p-table [columns]="SensorsAndGroupsCols" [value]="sensorsAndGroupsDisplayed" [scrollable]="true" scrollHeight="flex">
        <ng-template pTemplate="body" let-rowData let-columns="columns" let-i="i">
          <tr class="list-row">
            <td [ngStyle]="{'width': columns[0].width}" class="list-cell">
              <input *ngIf="rowData.isSensorGroup" type="checkbox" name="grp{{i}}" [(ngModel)]="rowData.checked" [ngModelOptions]="{standalone: true}"
                     [indeterminate]="rowData.indeterminate" (ngModelChange)="onGroupChecked(rowData)" style="cursor: pointer;" />
              <p-checkbox *ngIf="rowData.isSensor" id="{{i}}" name="{{i}}" [(ngModel)]="rowData.checked" binary="true" (ngModelChange)="sensorSelectionChanged()"></p-checkbox>
            </td>
            <td [ngStyle]="{'width': columns[1].width}" class="list-cell">
              <div *ngIf="rowData.isSensorGroup" class="group-label" (click)="toggleGroupExpanded(rowData)">
                <i class="fa" [ngClass]="rowData.expanded ? 'fa-chevron-down' : 'fa-chevron-right'" style="margin-right: 6px;"></i>
                <i class="fa fa-folder" style="margin-right: 5px;"></i>
                {{ rowData.name }} ({{ groupCheckedCount(rowData) }}/{{ rowData.count }})
              </div>
              <div *ngIf="rowData.isSensor" class="sensor-label">{{ rowData.name }}</div>
            </td>
          </tr>

          <ng-container *ngIf="rowData.isSensorGroup && rowData.expanded">
            <tr *ngFor="let child of rowData.children" class="list-row">
              <td [ngStyle]="{'width': columns[0].width}" class="list-cell">
                <p-checkbox name="grp{{i}}child{{child.id}}" [(ngModel)]="child.checked" [ngModelOptions]="{standalone: true}" binary="true" (ngModelChange)="onGroupChildChecked(rowData)"></p-checkbox>
              </td>
              <td [ngStyle]="{'width': columns[1].width}" class="list-cell child-cell">{{ child.name }}</td>
            </tr>
          </ng-container>
        </ng-template>
      </p-table>
    </div>
  </div>
</div>

<!-- Step 3: Select readings to chart -->
<div *ngIf="step === 3" class="wizard-step wizard-step-fill">
  <div class="step-title">{{ isEditMode ? 'Edit' : 'Create' }} Chart Favorite</div>
  <div class="step-subtitle">Select readings to chart</div>

  <div class="grow-panel list-panel" *ngIf="cf.selectedSensors.length > 0; else noSensors">
    <div class="list-panel-header">
      <label>Reading Types</label>
      <span class="list-panel-count">
        Selected Types: {{ cf.selectedReadingTypes.length }}
        <i class="fas fa-sync-alt" (click)="refreshReadings()" title="Reload the readings for the selected reading types"
           style="cursor: pointer; color: var(--kig-brand-blue); margin-left: 8px;"></i>
      </span>
    </div>

    <div class="list-panel-body">
      <div class="list-search-row">
        <div class="select-all-toggle" (click)="toggleCheckedReadings()" title="Select / clear all">▣</div>
        <i class="fa fa-search list-search-icon" (click)="readingTypeSearchTextChanged()"></i>
        <form (ngSubmit)="readingTypeSearchTextChanged()">
          <input [(ngModel)]="searchReadingTypeText" placeholder="Search" type="text" name="searchReadingTypeText" autocomplete="off" class="list-search-input" />
        </form>
      </div>

      <p-table [columns]="SensorReadingTypesCols" [value]="readingTypesDisplayed" [scrollable]="true" scrollHeight="flex">
        <ng-template pTemplate="body" let-rowData let-columns="columns" let-i="i">
          <tr class="list-row">
            <td [ngStyle]="{'width': columns[0].width}" class="list-cell">
              <p-checkbox id="{{i}}" name="{{i}}" [(ngModel)]="rowData.checked" binary="true" (ngModelChange)="readingTypeSelectionChanged()"></p-checkbox>
            </td>
            <td [ngStyle]="{'width': columns[1].width}" class="list-cell">{{ rowData.type }}</td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </div>

  <ng-template #noSensors>
    <div class="hint">Select one or more sensors in the previous step first.</div>
  </ng-template>

  <div class="hint">Only readings available for the selected sensors are shown.</div>
</div>

<!-- Step 4: Select data range -->
<div *ngIf="step === 4" class="wizard-step">
  <div class="step-title">{{ isEditMode ? 'Edit' : 'Create' }} Chart Favorite</div>
  <div class="step-subtitle">Select data range</div>

  <div class="date-panel">
    <div class="list-panel-header">
      <label>Date Range</label>
      <label class="list-panel-count">{{ cf.dateRangeName }}</label>
    </div>

    <div class="date-panel-body">
      <div class="quick-ranges">
        <button type="button" class="btn btn-sm quick-range-btn" (click)="datesUpdated('Last 4 hrs')">Last 4 hrs</button>
        <button type="button" class="btn btn-sm quick-range-btn" (click)="datesUpdated('Last 8 hrs')">Last 8 hrs</button>
        <button type="button" class="btn btn-sm quick-range-btn" (click)="datesUpdated('Last 12 hrs')">Last 12 hrs</button>
        <button type="button" class="btn btn-sm quick-range-btn" (click)="datesUpdated('Last 24 hrs')">Last 24 hrs</button>
      </div>

      <input type="text" name="wizardDaterange" class="daterangeinput" autocomplete="off"
             ngxDaterangepickerMd
             [(ngModel)]="cf.dateRange"
             [showCustomRangeLabel]="true"
             [alwaysShowCalendars]="true"
             [ranges]="ranges"
             [showRangeLabelOnInput]="true"
             [linkedCalendars]="true"
             [showClearButton]="false"
             (datesUpdated)="datesUpdated('')"
             placeholder="Select Date Range"
             (rangeClicked)="setRangeSelected($event)" />

      <div class="field-block">
        <label class="field-label">Displayed Timezone</label>
        <div class="radio-inline">
          <label><input type="radio" [(ngModel)]="cf.selectedTimeZone" name="wizardTz" value="Local" (ngModelChange)="selectedTimeZoneChanged()" /> Local</label>
          <label><input type="radio" [(ngModel)]="cf.selectedTimeZone" name="wizardTz" value="Source" (ngModelChange)="selectedTimeZoneChanged()" /> Source</label>
          <label><input type="radio" [(ngModel)]="cf.selectedTimeZone" name="wizardTz" value="UTC" (ngModelChange)="selectedTimeZoneChanged()" /> UTC (GMT+0)</label>
        </div>
      </div>

      <div class="field-block">
        <label class="field-label">Readings</label>
        <div class="radio-inline">
          <label><input type="checkbox" [(ngModel)]="cf.showOriginalReadings" name="wizardOrig" (ngModelChange)="selectedReadingSourceChanged()" /> Original Values</label>
          <label><input type="checkbox" [(ngModel)]="cf.showCalibratedReadings" name="wizardCal" (ngModelChange)="selectedReadingSourceChanged()" /> Calibrated Values</label>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Step 5: Save -->
<div *ngIf="step === 5" class="wizard-step">
  <div class="step-title">{{ isEditMode ? 'Edit' : 'Create' }} Chart Favorite</div>

  <div class="step-subtitle">Save Chart Favorite</div>
  <div class="form-group">
    <label>Chart Favorite Name</label>
    <input type="text" name="wizardFavoriteName" class="form-control" placeholder="Chart Favorites"
           [class.input-error]="favoriteNameTaken"
           [(ngModel)]="cf.favoriteName" (ngModelChange)="onSaveFieldChanged()" />
    <div class="error-hint" *ngIf="favoriteNameTaken">A chart favorite with this name already exists.</div>
  </div>
  <div class="form-group">
    <label>Access Group</label>
    <ngx-select-dropdown [(ngModel)]="cf.favoriteUserGroup" [config]="userGroupConfig"
                         [options]="associatedUserGroups" name="wizardFavoriteGroup"></ngx-select-dropdown>
  </div>

  <ng-container *ngIf="!isEditMode">
    <div class="step-subtitle" style="margin-top: 18px;">Create or update dashboard</div>
    <div class="form-group radio-row">
      <label>
        <input type="radio" name="wizardDashTarget" value="new"
               [(ngModel)]="cf.dashboardTargetMode" (ngModelChange)="onSaveFieldChanged()" /> Add to new dashboard
      </label>
      <label>
        <input type="radio" name="wizardDashTarget" value="existing"
               [(ngModel)]="cf.dashboardTargetMode" (ngModelChange)="onSaveFieldChanged()" /> Add to existing dashboard
      </label>
    </div>

    <div class="form-group" *ngIf="cf.dashboardTargetMode === 'new'">
      <input type="text" name="wizardNewDashName" class="form-control" placeholder="New dashboard name"
             [class.input-error]="newDashboardNameTaken"
             [(ngModel)]="cf.newDashboardName" (ngModelChange)="onSaveFieldChanged()" />
      <div class="error-hint" *ngIf="newDashboardNameTaken">A dashboard with this name already exists.</div>
    </div>
    <div class="form-group" *ngIf="cf.dashboardTargetMode === 'existing'">
      <ng-select [items]="dashboards"
                 bindLabel="name"
                 placeholder="Select Dashboard"
                 [(ngModel)]="cf.targetDashboard"
                 (ngModelChange)="onSaveFieldChanged()">
      </ng-select>
    </div>
  </ng-container>

  <div class="hint" *ngIf="isEditMode">Finish will update this chart favorite.</div>
</div>
